import { apm, init as initApm } from '@elastic/apm-rum'
import { Environment, ErrorBoundary, HubConfig, MySquid, User, onLangChange, onUserChange } from '@hub/shared'
import CampaignListType from '@pages/campaign-list-type/campaign-list-type.page'
import CampaignDetailsPage from '@pages/details/details.page'
import CampaignsHomePage from '@pages/home-campaign/home-campaign.page'
import CampaignSummaryPage from '@pages/summary/summary.page'
import { onLangChange as onLangReactCss } from '@squidit/react-css'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import i18n from './i18n.campaigns'

import './root.component.scss'

interface UserObject {
  id?: string | number
  username?: string
  email?: string
}

export default function Root(props: {
  configs: MySquid
  environments: Environment
  general: HubConfig
  mountParcel: any
  name: string
  singleSpa: any
  domElement: Element
  mainRoutes: string[]
}) {
  const { t } = useTranslation()
  useEffect(() => {
    const onLangChangeObservable: SubscriptionLike = onLangChange.subscribe((lang: 'pt' | 'en' | 'es') => {
      i18n.changeLanguage(lang)
      onLangReactCss.next(lang)
    })
    initApm({
      environment: props?.environments?.env,
      serviceName: 'web-hub',
      serverUrl: props?.environments?.apmUrl,
    })
    const onUserChangeObservable: SubscriptionLike = onUserChange.subscribe(async (userChanged: User) => {
      const userContext: UserObject = {
        id: userChanged?.squidId,
        username: userChanged?.profileadditionalinfo?.name,
        email: userChanged?.profileadditionalinfo?.email,
      }
      apm.setUserContext(userContext)
    })
    return () => {
      onLangChangeObservable?.unsubscribe()
      onUserChangeObservable?.unsubscribe()
    }
  }, [props?.environments])

  const NoMatch = () => {
    history.pushState({}, '', '/error/not-found')
    return <div />
  }

  const RedirectTo = ({ path = '' }) => {
    const { idCampaign }: any = useParams()
    return <Navigate to={path.replace(':idCampaign', idCampaign)} />
  }

  const RedirectCampaign = (props: any) => {
    return (
      <Navigate
        to={{
          pathname: `/campaigns/${props.match.params.idCampaign}/summary`,
          search: window.location.search,
        }}
      />
    )
  }

  if (props?.general?.maintenance) {
    history.pushState({}, '', '/error/maintenance')
    return <div />
  }

  return (
    <ErrorBoundary fallback={t('globalCampaigns:errorText')}>
      <Router>
        <Routes>
          <Route path="campaigns" element={<CampaignsHomePage {...props} />} />
          <Route path="campaigns/:campaignType" element={<CampaignListType {...props} />} />
          <Route path="campaigns/:idCampaign/summary" element={<CampaignSummaryPage {...props} />} />
          <Route path="campaigns/:idCampaign/details/:idSocial/*" element={<CampaignDetailsPage {...props} />} />
          <Route
            path="dashboard/campaigns/details/:idCampaign"
            element={<RedirectTo path={`/campaigns/:idCampaign/summary${window.location.search}`} />}
          />
          <Route
            path="campaigns/details/:idCampaign"
            element={<RedirectTo path={`/campaigns/:idCampaign/summary${window.location.search}`} />}
          />
          <Route path="campaigns/:idCampaign" element={<RedirectCampaign />} />
          <Route
            path="dashboard/campaigns/proposal"
            element={
              <Navigate
                to={{
                  pathname: '/campaigns/invited',
                  search: window.location.search,
                }}
              />
            }
          />
          <Route
            path="dashboard/campaigns/finished"
            element={
              <Navigate
                to={{
                  pathname: '/campaigns/finished',
                  search: window.location.search,
                }}
              />
            }
          />
          <Route
            path="dashboard/campaigns/active"
            element={
              <Navigate
                to={{
                  pathname: '/campaigns/participating',
                  search: window.location.search,
                }}
              />
            }
          />
          <Route
            path="dashboard/campaigns"
            element={
              <Navigate
                to={{
                  pathname: '/campaigns',
                  search: window.location.search,
                }}
              />
            }
          />
          <Route
            path="dashboard"
            element={
              <Navigate
                to={{
                  pathname: '/campaigns',
                  search: window.location.search,
                }}
              />
            }
          />
          {props?.mainRoutes?.map((r) => {
            return <Route path={`${r}/*`} key={r} />
          })}
          <Route path="*" element={<NoMatch />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  )
}
